import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listAccounts } from '../../services/accountsApi';
import type { Account } from '../../types/account';

interface ClientDropdownProps {
  onSelect: (clientId: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
  includeInactive?: boolean; // retained for API compat
}

/**
 * Searchable client dropdown — loads all active clients upfront, filters client-side.
 * Feature: 018-edit-client-association US1
 */
export default function ClientDropdown({
  onSelect,
  placeholder = 'Search accounts...',
  autoFocus = false,
  className = '',
  includeInactive: _includeInactive = false,
}: ClientDropdownProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const inputRef   = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load all active accounts once on mount
  const { data: accounts = [], isLoading, error } = useQuery<Account[]>({
    queryKey: ['accounts-all-active'],
    queryFn: () => listAccounts({ status: 'ACTIVE' }),
    staleTime: 60_000,
  });

  // Client-side filtering — show all when search is empty
  const filtered = searchTerm.trim().length > 0
    ? accounts.filter(a =>
        a.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.email?.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : accounts;

  // Auto-focus and open on mount if requested
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
      setIsOpen(true);
    }
  }, [autoFocus]);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (account: Account) => {
    onSelect(account.accountId);
    setSearchTerm(account.companyName ?? account.accountId);
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Search Input */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          onFocus={() => setIsOpen(true)}
          placeholder={isLoading ? 'Loading accounts…' : placeholder}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        />
        {/* Icon — spinner while loading, magnifier otherwise */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          {isLoading ? (
            <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ) : (
            <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          )}
        </div>
      </div>

      {/* Dropdown Results */}
      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
          {error ? (
            <div className="px-3 py-2 text-sm text-red-600">Error loading clients. Please try again.</div>
          ) : isLoading ? (
            <div className="px-3 py-2 text-sm text-gray-500">Loading clients…</div>
          ) : filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">
              {searchTerm ? `No accounts match "${searchTerm}"` : 'No active accounts found'}
            </div>
          ) : (
            filtered.map(account => (
              <button
                key={account.accountId}
                type="button"
                onClick={() => handleSelect(account)}
                className="w-full text-left px-3 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
              >
                <div className="flex flex-col">
                  <span className="font-medium text-gray-900">
                    {account.companyName ?? 'Unnamed Company'}
                  </span>
                  {account.email && (
                    <span className="text-xs text-gray-500">{account.email}</span>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}


interface ClientDropdownProps {
  onSelect: (clientId: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
  includeInactive?: boolean; // retained for API compat
}
